import { Injectable, inject } from '@angular/core';
import {
  HttpHeaders,
  HttpClient, HttpParams
} from '@angular/common/http';

import {environment} from '@environments/environment';

/** Template Management's bulk actions. */
export type TemplateBulkAction = 'show' | 'hide' | 'thumbnails' | 'source' | 'delete';

/** What a bulk job did: how many templates ended in each outcome, and why the first few failed. */
export interface TemplateBulkResult {
  action: TemplateBulkAction;
  counts: {[outcome: string]: number};
  failures: {id: number; message: string}[];
}

/** Where a bulk job stands. `state` is the job's: waiting, active, completed, failed, ... */
export interface TemplateBulkStatus {
  jobId: string;
  state: string;
  progress: number;
  result: TemplateBulkResult | null;
  failedReason: string | null;
}

@Injectable({providedIn: 'root'})
export class TemplateService {
  private http = inject(HttpClient);

  private baseApiUrl = environment.apiUrl;
  private templateUrl = this.baseApiUrl + '/api/v1/template';
  private templateTypeUrl = this.baseApiUrl + '/api/v1/template_type';
  private templateSubTypeUrl = this.baseApiUrl + '/api/v1/template_sub_type';
  private visualEditorUrl = this.baseApiUrl + '/api/v1/visual-editor';
  private widgetAvatarTemplateUrl = this.baseApiUrl + '/api/v1/widget_avatar_template';
  private shareUrl = this.baseApiUrl + '/api/v1/share';
  private apiUrl = this.baseApiUrl + '/api/v1';

  getAll(params?: any) {
    return this.http.get(`${this.templateUrl}`, {params});
  }

  touch(body: any) {
    return this.http.post(`${this.templateUrl}/touch`, body);
  }

  get(id: number) {
    return this.http.get(`${this.templateUrl}/${id}`);
  }

  preview(id: number, item) {
    return this.http.post(`${this.templateUrl}/${id}/preview`, item);
  }

  previewUrl(id: number) {
    return this.http.post(`${this.templateUrl}/${id}/preview`, {});
  }

  /** Sends an email template to these addresses, every variable with its sample value. */
  sendTestEmail(id: number, email: string[]) {
    return this.http.post(`${this.templateUrl}/${id}/preview`, {email});
  }

  renderPreview(id: number, params: {page?: number; body_json?: any; visual_editor_id?: number | null; emails_source_message?: string; sender?: {fromName: string; fromEmail: string; replyTo: string; subject: string}; navigation?: string; render_id?: number}) {
    return this.http.post(`${this.templateUrl}/${id}/render-preview`, params);
  }

  sourceCompare(id: number, pending: {title?: string; subject?: string; body_json?: any; revision_id?: number | null} = {}) {
    return this.http.post(`${this.templateUrl}/${id}/source-compare`, pending);
  }

  sourceVersions(id: number) {
    return this.http.get(`${this.templateUrl}/${id}/source-compare/versions`);
  }

  widgetPreviewUrl(id: number, genericShare?: boolean) {
    return this.http.post(`${this.shareUrl}/${id}/preview`, {genericShare});
  }

  updateStatus(id: number, status: string) {
    return this.http.put(`${this.templateUrl}/${id}`, {status});
  }

  updateAll(id: number, params: any) {
    return this.http.put(`${this.templateUrl}/${id}`, params);
  }

  deleteTemplate(id: number) {
    return this.http.delete(`${this.templateUrl}/${id}`);
  }

  update(id: number, body: any) {
    return this.http.put(`${this.templateUrl}/${id}`, body);
  }

  create(body: any) {
    return this.http.post(`${this.templateUrl}`, body);
  }

  indexForCampaign(params) {
    return this.http.get(`${this.templateUrl}/campaign`, {params});
  }

  customScriptInstructions(version?: string) {
    let params = new HttpParams();
    if (version) {
      params = params.set('version', version);
    }
    return this.http.get(`${this.shareUrl}/custom_script_instruction`, {params});
  }

  /** Runs a bulk action on these templates (at most 5000) as a job: answers its id. */
  bulk(action: TemplateBulkAction, ids: number[]) {
    return this.http.post<{data: {jobId: string}}>(`${this.templateUrl}/bulk`, {action, ids});
  }

  /** Where a bulk job this user started stands. */
  bulkStatus(jobId: string) {
    return this.http.get<{data: TemplateBulkStatus}>(`${this.templateUrl}/bulk/${encodeURIComponent(jobId)}`);
  }

  // params.context 'create' leaves out the protected types; 'edit' with template_id keeps
  // only the protected type that template already has.
  getTemplateTypes(params?: any) {
    return this.http.get(`${this.templateTypeUrl}`, {params});
  }

  getTemplateSubTypes() {
    return this.http.get(`${this.templateSubTypeUrl}`);
  }

  getVisualEditors() {
    return this.http.get(`${this.visualEditorUrl}`);
  }

  getTemplateGetRevisions(templateId, params?: any) {
    return this.http.get(`${this.templateUrl}/${templateId}/revisions`, {params});
  }

  getAllWidgetAvatarTemplates(params?: any) {
    return this.http.get(`${this.widgetAvatarTemplateUrl}`, {params});
  }

  createWidgetAvatarTemplate(body: any) {
    return this.http.post(`${this.widgetAvatarTemplateUrl}`, body);
  }

  updateWidgetAvatarTemplate(id: number, body: any) {
    return this.http.put(`${this.widgetAvatarTemplateUrl}/${id}`, body);
  }

  deleteWidgetAvatarTemplate(id: number) {
    return this.http.delete(`${this.widgetAvatarTemplateUrl}/${id}`);
  }

  generateWidgetAvatars(body: any) {
    return this.http.post(`${this.apiUrl}/ai/generate_widget_avatar`, body);
  }

  getAllSocialImageSize(params?: any) {
    return this.http.get(`${this.templateUrl}/social_image_size`, {params});
  }
}
